import { componentManager } from "./ComponentManager";
import { getElementInternals, setElementInternals } from "./elementInternals";
import "./manifest.gen";
import { registerMountPoint, unregisterMountPoint } from "./mountPointStore";
import { camelToKebab, kebabToCamel, tagNameFor } from "./tagName";
import type { ComponentDefinition, ComponentName, InertTagName } from "./types";

const titleContent = new WeakMap<HTMLElement, string>();

// For components that declare a `title` prop the attribute is content, not the
// browser's advisory tooltip. Left in place it would show a native tooltip over
// the whole component - and over anything it projects through a slot, since
// that content stays a DOM descendant of the host.

function takeTitleContent(el: HTMLElement, raw: Record<string, unknown>): void {
  const attribute = el.getAttribute("title");

  if (attribute !== null) {
    titleContent.set(el, attribute);
    el.removeAttribute("title");
  }

  const content = titleContent.get(el);

  if (content === undefined) {
    delete raw.title;

    return;
  }

  raw.title = content;
}

function readProps(
  el: HTMLElement,
  componentName: ComponentName,
  { dataSchema, usesSlots, readSlotted }: ComponentDefinition,
): Record<string, unknown> {
  const raw: Record<string, unknown> = {};

  Array.from(el.attributes).forEach((attr) => {
    raw[kebabToCamel(attr.name)] = attr.value;
  });

  if ("title" in dataSchema.shape) {
    takeTitleContent(el, raw);
  }

  if (!usesSlots) {
    if (el.children.length > 0) {
      console.warn(
        `[Bridge] HTML children are not supported on <${tagNameFor(componentName)}>`,
      );
    } else {
      const text = el.textContent?.trim();

      if (text) {
        raw.label = text;
      }
    }
  }

  const props = dataSchema.parse(raw);

  if (usesSlots) {
    // Light-DOM children are re-projected via native <slot> elements. Merged
    // in after parsing, so slotted props don't need a dataSchema entry just
    // to survive Zod's default key-stripping.

    Object.assign(props, readSlotted?.(el));
  }

  return props;
}

function defineElement(name: ComponentName | InertTagName): void {
  const tagName = tagNameFor(name);

  // Already defined (e.g. by a previous call to defineRWCElements)
  if (customElements.get(tagName)) {
    return;
  }

  const maybeDefinition = componentManager.getDefinition(name);

  if (!maybeDefinition) {
    customElements.define(tagName, class extends HTMLElement {});
    return;
  }

  // Capture the definition with its narrowed type instead
  const definition: ComponentDefinition = maybeDefinition;

  const componentName = name as ComponentName;

  class RWCElement extends HTMLElement {
    static get observedAttributes(): string[] {
      return Object.keys(definition.dataSchema.shape).map(camelToKebab);
    }

    static formAssociated = definition.formAssociated ?? false;

    #connected = false;
    #observer: MutationObserver | undefined;
    #syncScheduled = false;

    constructor() {
      super();

      if (definition.formAssociated) {
        setElementInternals(this, this.attachInternals());
      }
    }

    get form(): HTMLFormElement | null {
      return getElementInternals(this)?.form ?? null;
    }

    connectedCallback() {
      this.#connected = true;

      // Synchronous on purpose: initial upgrade happens before the flushSync
      // render (see index.tsx), which must see this mount point immediately.
      this.#sync();

      if (definition.usesSlots) {
        // Re-derive props when direct light-DOM children are added or removed.
        this.#observer = new MutationObserver(() => this.#scheduleSync());
        this.#observer.observe(this, { childList: true });
      }
    }

    attributeChangedCallback(
      _name: string,
      oldValue: string | null,
      newValue: string | null,
    ) {
      // Fires this even when the value is unchanged
      if (this.#connected && oldValue !== newValue) {
        this.#scheduleSync();
      }
    }

    // A microtask so it still runs before the browser paints
    #scheduleSync() {
      if (this.#syncScheduled) {
        return;
      }

      this.#syncScheduled = true;

      queueMicrotask(() => {
        this.#syncScheduled = false;

        if (this.#connected) {
          this.#sync();
        }
      });
    }

    disconnectedCallback() {
      this.#connected = false;
      this.#observer?.disconnect();
      unregisterMountPoint(this);
    }

    #sync() {
      const props = readProps(this, componentName, definition);

      if (!definition.usesSlots) {
        this.replaceChildren();
      }

      registerMountPoint({ el: this, componentName, props });
    }
  }

  customElements.define(tagName, RWCElement);
}

export function defineRWCElements(): void {
  componentManager.names().forEach(defineElement);
}
